/**
 * @license
 * Copyright 2026 okayest! studios LLC. All Rights Reserved.
 */

/**
 * @fileoverview Exports row counters as a Yarnly project archive, in the
 * format the Yarnly app imports (see export.dart and import.dart in the app).
 */

import { strToU8, zipSync } from 'fflate';

import { RowCounterState } from './counter-model';
import { newId } from './id';

const defaultTitle = 'Row Counter Project';
/** CraftType.crochet, the app's default craft type. */
const defaultCraft = 1;
/** ProjectStatus.inProgress. */
const defaultStatus = 0;
const externalIdLength = 12;

/** Builds the project JSON, matching the drift Project.toJson() format. */
export function buildYarnlyProject(state: RowCounterState, externalId: string, now: Date): Record<string, unknown> {
  const created = Math.min(now.getTime(), ...state.counters.map((c) => c.updated).filter((t) => t > 0));
  return {
    id: 0,
    externalId,
    title: defaultTitle,
    created,
    modified: now.getTime(),
    status: defaultStatus,
    craft: defaultCraft,
    counters: {
      counters: state.counters.map((c) => ({
        id: c.id,
        rows: c.rows,
        label: c.label,
        ...(c.parentId !== null ? { parentId: c.parentId } : {}),
        ...(c.rowsInRepeat !== null ? { rowsInRepeat: c.rowsInRepeat } : {}),
        updated: new Date(c.updated > 0 ? c.updated : now.getTime()).toISOString(),
        startAtOne: c.startAtOne,
      })),
      ...(state.lastUsedCounterId !== null ? { lastUsedCounterId: state.lastUsedCounterId } : {}),
    },
  };
}

/**
 * Downloads the counters as a .zip that can be imported into Yarnly, returning
 * the downloaded file name.
 */
export function downloadYarnlyExport(state: RowCounterState): string {
  const now = new Date();
  const externalId = newId(externalIdLength);
  const project = buildYarnlyProject(state, externalId, now);
  const dir = `project-${externalId}`;
  const zip = zipSync({
    [`${dir}/${dir}.json`]: strToU8(JSON.stringify(project, null, 2)),
  });

  const fileName = `yarnly-row-counters-${now.toISOString().slice(0, 10)}.zip`;
  const url = URL.createObjectURL(new Blob([zip as BlobPart], { type: 'application/zip' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return fileName;
}
